import { date, number, string } from 'yup'; // eslint-disable-line id-blacklist

import { IValidationError, IValidator, IValidatorFactory, NO_ERROR, NoErrorType } from './additional-info.types';
import { FIELD_VIEW_TYPES, TEXT_SUB_KEYS } from './additional-info.constants';
import { IFieldRenderParams } from './fields/fields.types';

export const validateDate: IValidator<string> = (value = '') => {
    const [year, month, day] = value.split('-');

    if (value && date().isValidSync(new Date(`${month}/${day}/${year}`))) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.IS_NOT_DATE_ERROR
    };
};

export const required: IValidator<string> = value => {
    if (value) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.REQUIRED_ERROR
    };
};

export const composeValidators = (...validators: Array<IValidator<string>>): IValidator<string> => value =>
    validators.reduce((error: NoErrorType | IValidationError, validator) => error || validator(value), NO_ERROR);

export const getFieldValidators = (field: IFieldRenderParams): IValidator<string> => {
    let validators: IValidator<string>[] = [];

    if (field.type === FIELD_VIEW_TYPES.DATE) {
        validators.push(validateDate);
    }

    if (field.isRequired) {
        validators.push(required);
    }

    if (field.type === FIELD_VIEW_TYPES.INT) {
        validators.push(intValidator);
    }

    if (field.minNumber) {
        validators.push(minValidatorFactory(field.minNumber));
    }

    if (field.maxNumber) {
        validators.push(maxValidatorFactory(field.maxNumber));
    }

    if (field.minStringLength) {
        validators.push(minLengthValidatorFactory(field.minStringLength));
    }

    if (field.maxStringLength) {
        validators.push(maxLengthValidatorFactory(field.maxStringLength));
    }

    return composeValidators(...validators);
};

export const minValidatorFactory: IValidatorFactory<number, string> = min => value => {
    const isFilledString = string()
        .min(1)
        .isValidSync(value);

    const isInLimit = number()
        .min(min)
        .isValidSync(+value);

    if (isFilledString && isInLimit) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.NUMBER_MIN_ERROR,
        data: { min }
    };
};

export const maxValidatorFactory: IValidatorFactory<number, string> = max => value => {
    const isFilledString = string()
        .min(1)
        .isValidSync(value);

    const isInLimit = number()
        .max(max)
        .isValidSync(+value);

    if (isFilledString && isInLimit) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.NUMBER_MAX_ERROR,
        data: { max }
    };
};

export const intValidator: IValidator<string> = value => {
    const isFilledString = string()
        .min(1)
        .isValidSync(value);

    const isIntegerValue = Number.isInteger(+value);

    if (isFilledString && isIntegerValue) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.IS_NOT_NUMBER_ERROR
    };
};

export const minLengthValidatorFactory: IValidatorFactory<number, string> = min => value => {
    const isLengthInLimit = string()
        .min(min)
        .isValidSync(value);

    if (isLengthInLimit) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.LENGTH_MIN_ERROR,
        data: { min }
    };
};

export const maxLengthValidatorFactory: IValidatorFactory<number, string> = max => value => {
    const isLengthInLimit = string()
        .max(max)
        .isValidSync(value);

    if (isLengthInLimit) {
        return NO_ERROR;
    }
    return {
        message: TEXT_SUB_KEYS.LENGTH_MAX_ERROR,
        data: { max }
    };
};
